<script setup>
import { useAbility } from '@casl/vue'
import { useDeliveryPlatformsStore } from '@/stores/deliveryPlatforms'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Settings',
  },
})

const ability = useAbility()
const store = useDeliveryPlatformsStore()
const { loading, request } = useApiRequest()

const headers = [
  { title: 'Nama', key: 'name' },
  { title: 'Kode', key: 'code' },
  { title: 'Komisi', key: 'commission_percentage' },
  { title: 'Status', key: 'is_active' },
  { title: 'Aksi', key: 'actions', sortable: false, width: '110px' },
]

const platformCodeColor = code => ({
  gofood: 'success',
  grabfood: 'success',
  shopeefood: 'error',
  other: 'grey',
}[code] ?? 'grey')

const formDialog = ref(false)
const editingPlatform = ref(null)
const deleteConfirm = ref(false)
const deletingPlatform = ref(null)

const openCreate = () => {
  editingPlatform.value = null
  formDialog.value = true
}

const openEdit = platform => {
  editingPlatform.value = platform
  formDialog.value = true
}

const confirmDelete = platform => {
  deletingPlatform.value = platform
  deleteConfirm.value = true
}

const handleDelete = async () => {
  await request(async () => {
    await store.deletePlatform(deletingPlatform.value.id)
    deleteConfirm.value = false
    await store.fetchPlatforms()
  })
}

onMounted(() => store.fetchPlatforms())
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12" class="d-flex align-center flex-wrap gap-3">
        <div>
          <h4 class="text-h4 font-weight-medium mb-1">Platform Delivery</h4>
          <p class="text-body-2 text-medium-emphasis mb-0">
            Kelola integrasi ojek online (GoFood, GrabFood, ShopeeFood, dll)
          </p>
        </div>
        <VSpacer />
        <VBtn
          v-if="ability.can('update', 'Settings')"
          color="primary"
          prepend-icon="ri-add-line"
          @click="openCreate"
        >
          Tambah Platform
        </VBtn>
      </VCol>
    </VRow>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="store.platforms"
        :loading="store.loading"
        hover
      >
        <template #item.code="{ item }">
          <VChip :color="platformCodeColor(item.code)" size="small" variant="tonal">
            {{ item.code }}
          </VChip>
        </template>

        <template #item.commission_percentage="{ item }">
          {{ item.commission_percentage != null ? `${item.commission_percentage}%` : '-' }}
        </template>

        <template #item.is_active="{ item }">
          <VChip :color="item.is_active ? 'success' : 'error'" size="small" variant="tonal">
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('update', 'Settings')"
            icon
            size="small"
            variant="text"
            @click="openEdit(item)"
          >
            <VIcon icon="ri-edit-line" />
          </VBtn>
          <VBtn
            v-if="ability.can('update', 'Settings')"
            icon
            size="small"
            variant="text"
            color="error"
            @click="confirmDelete(item)"
          >
            <VIcon icon="ri-delete-bin-line" />
          </VBtn>
        </template>

        <template #no-data>
          <div class="text-center pa-8">
            <VIcon icon="ri-motorbike-line" size="64" color="grey-lighten-1" />
            <p class="text-body-1 mt-4">Belum ada platform delivery</p>
            <VBtn
              v-if="ability.can('update', 'Settings')"
              color="primary"
              class="mt-2"
              @click="openCreate"
            >
              Tambah Platform Pertama
            </VBtn>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <DeliveryPlatformFormDialog
      v-model="formDialog"
      :platform="editingPlatform"
      @saved="store.fetchPlatforms"
    />

    <ConfirmDialog
      v-model="deleteConfirm"
      title="Hapus Platform?"
      :message="`Platform ${deletingPlatform?.name} akan dihapus permanen.`"
      confirm-text="Hapus"
      color="error"
      :loading="loading"
      @confirm="handleDelete"
    />
  </div>
</template>
